import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  Form,
  Grid,
  Input,
  List,
  Radio,
  Row,
  Space,
  Switch,
  Tabs,
  Tag,
  Typography,
  message,
} from 'antd';
import type { TabsProps } from 'antd';
import {
  UserOutlined,
  EditOutlined,
  LockOutlined,
  UploadOutlined,
  EyeOutlined,
  HistoryOutlined,
  IdcardOutlined,
  SaveOutlined,
  RocketOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { trpc } from '../../lib/api';
import { useUserPreferences } from '../../contexts/UserPreferencesContext';
import type { UserPreferences } from '../../contexts/UserPreferencesContext';
import { getErrorMessage } from '../../utils/errorMessage';
import BalancedDescriptions from '../common/BalancedDescriptions';
import { AvatarUploadModal } from './AvatarUploadModal';
import { getEffectiveAvatarUrl } from '../../utils/avatar';
import { phoneNumberRule } from '@/constants/validation';
import { PhoneInput } from '../common';
import { DocumentListSection } from '../document/DocumentListSection';

interface ProfileFormValues extends Record<string, unknown> {
  avatarUrl?: string | null;
}

interface PasswordFormValues {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}


const { Text, Title, Paragraph } = Typography;

const formatDateTime = (value?: string | null) =>
  value ? dayjs(value).format('YYYY/MM/DD HH:mm') : '-';

const getRoleText = (role: string) => {
  switch (role) {
    case 'admin':
      return '管理者';
    case 'manager':
      return 'マネージャー';
    case 'user':
      return 'ユーザー';
    case 'viewer':
      return '閲覧者';
    default:
      return role;
  }
};

const ProfileManagement: React.FC = () => {
  const [profileForm] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [displayForm] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [editing, setEditing] = useState(false);
  const [activeTabKey, setActiveTabKey] = useState<string>('basic');
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const utils = trpc.useUtils();
  const screens = Grid.useBreakpoint();
  const columnCount = screens.md || screens.lg || screens.xl || screens.xxl ? 2 : 1;

  const { preferences, replacePreferences } = useUserPreferences();

  const { data: user, isLoading } = trpc.user.getProfile.useQuery();

  // デモアカウントで admin 以外の場合は読み取り専用
  const isDemoReadOnly = useMemo(() => {
    return user?.isDemo === true && user?.role !== 'admin';
  }, [user?.isDemo, user?.role]);

  const effectiveAvatarUrl = useMemo(() => getEffectiveAvatarUrl(user), [user]);

  const updateProfileMutation = trpc.user.updateProfile.useMutation({
    onSuccess: (updatedUser) => {
      messageApi.success('プロフィールを更新しました');
      if (updatedUser) {
        profileForm.setFieldsValue({
          ...updatedUser,
          avatarUrl: getEffectiveAvatarUrl(updatedUser) ?? undefined,
        });
      }
      setEditing(false);
      utils.user.getProfile.invalidate();
    },
    onError: (error: unknown) => {
      messageApi.error(`更新に失敗しました: ${getErrorMessage(error, '詳細不明')}`);
    },
  });

  const uploadAvatarMutation = trpc.user.uploadAvatar.useMutation({
    onSuccess: (result) => {
      messageApi.success('アバター画像を更新しました');
      if (typeof result?.avatarUrl === 'string') {
        utils.user.getProfile.setData(undefined, (current) => {
          if (!current) {
            return current;
          }

          const currentPreferences =
            typeof current.preferences === 'object' && current.preferences !== null
              ? { ...(current.preferences as Record<string, unknown>) }
              : {};

          return {
            ...current,
            avatarUrl: result.avatarUrl,
            preferences: {
              ...currentPreferences,
              avatarUrl: result.avatarUrl,
            },
          };
        });
        profileForm.setFieldsValue({ avatarUrl: result.avatarUrl });
      }
      utils.user.getProfile.invalidate();
    },
    onError: (error: unknown) => {
      messageApi.error(`アバターのアップロードに失敗しました: ${getErrorMessage(error, '詳細不明')}`);
    },
  });

  const changePasswordMutation = trpc.user.changePassword.useMutation({
    onSuccess: () => {
      messageApi.success('パスワードを変更しました');
      passwordForm.resetFields();
    },
    onError: (error: unknown) => {
      messageApi.error(`パスワード変更に失敗しました: ${getErrorMessage(error, '詳細不明')}`);
    },
  });

  const updatePreferencesMutation = trpc.user.updateProfile.useMutation({
    onSuccess: (updatedUser) => {
      messageApi.success('表示設定を更新しました');
      replacePreferences(updatedUser?.preferences as Partial<UserPreferences>);
      utils.user.getProfile.invalidate();
    },
    onError: (error: unknown) => {
      messageApi.error(`表示設定の更新に失敗しました: ${getErrorMessage(error, '詳細不明')}`);
    },
  });

  useEffect(() => {
    if (user && editing && activeTabKey === 'basic') {
      profileForm.setFieldsValue({
        ...user,
        avatarUrl: effectiveAvatarUrl ?? undefined,
      });
    }
  }, [user, editing, profileForm, activeTabKey, effectiveAvatarUrl]);

  useEffect(() => {
    if (activeTabKey !== 'appearance') {
      return;
    }

    displayForm.setFieldsValue({
      appearance: preferences.appearance,
    });
  }, [preferences, displayForm, activeTabKey]);

  const handleProfileSubmit = async (values: ProfileFormValues) => {
    const cleanedValues = { ...values };
    if (cleanedValues.avatarUrl === '') {
      delete cleanedValues.avatarUrl;
    }
    await updateProfileMutation.mutateAsync(cleanedValues);
  };

  const handleCancel = () => {
    if (user) {
      profileForm.setFieldsValue({
        ...user,
        avatarUrl: effectiveAvatarUrl ?? undefined,
      });
    }
    setEditing(false);
  };

  const handlePasswordSubmit = async (values: PasswordFormValues) => {
    await changePasswordMutation.mutateAsync(values);
  };

  const handleAvatarUploadSuccess = async (avatarBase64: string) => {
    await uploadAvatarMutation.mutateAsync({ imageData: avatarBase64 });
  };

  const handleDisplaySubmit = async (values: Partial<UserPreferences>) => {
    const nextPreferences: UserPreferences = {
      ...preferences,
      appearance: {
        ...preferences.appearance,
        ...values.appearance,
      },
    };

    await updatePreferencesMutation.mutateAsync({ preferences: nextPreferences });
  };


  const activityEntries = useMemo(() => {
    if (!user) {
      return [];
    }
    return [
      {
        title: '最終ログイン',
        timestamp: user.lastLoginAt,
        description: user.loginCount ? `ログイン回数: ${user.loginCount}` : undefined,
      },
      {
        title: 'プロフィール最終更新',
        timestamp: user.updatedAt,
      },
      {
        title: 'アカウント作成',
        timestamp: user.createdAt,
      },
    ];
  }, [user]);

  if (isLoading) {
    return <div>プロフィールを読み込み中...</div>;
  }

  if (!user) {
    return <div>ユーザー情報が見つかりません</div>;
  }

  const basicInfoContent = (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {isDemoReadOnly && (
        <Alert
          message="デモアカウント"
          description="このアカウントは閲覧専用です。プロフィールの編集はできません。"
          type="info"
          showIcon
        />
      )}
      <Space align="start" size="large" wrap>
        <div style={{ position: 'relative', display: 'inline-block' }}>
          <Avatar
            src={effectiveAvatarUrl ?? undefined}
            icon={<UserOutlined />}
            size={120}
            style={{ border: '2px solid var(--ant-primary-color)' }}
          />
          {!isDemoReadOnly && (
            <Button
              type="primary"
              size="small"
              icon={<UploadOutlined />}
              onClick={() => setAvatarModalOpen(true)}
              style={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                borderRadius: '50%',
                width: 32,
                height: 32,
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            />
          )}
        </div>
        <Space direction="vertical" size={8}>
          <Title level={4} style={{ margin: 0 }}>
            {user.name}
          </Title>
          <Space size={[8, 8]} wrap>
            <Tag color="blue">{getRoleText(user.role)}</Tag>
            {user.department && <Tag>{user.department}</Tag>}
            {user.position && <Tag>{user.position}</Tag>}
          </Space>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            最終ログイン: {formatDateTime(user.lastLoginAt)}
          </Paragraph>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>
            アカウント作成: {formatDateTime(user.createdAt)}
          </Paragraph>
        </Space>
        {!isDemoReadOnly && (
          <>
            <Button
              type={editing ? 'default' : 'primary'}
              icon={editing ? <SaveOutlined /> : <EditOutlined />}
              onClick={() => {
                if (editing) {
                  profileForm.submit();
                } else {
                  setEditing(true);
                  profileForm.setFieldsValue({
                    ...user,
                    avatarUrl: effectiveAvatarUrl ?? undefined,
                  });
                }
              }}
              loading={updateProfileMutation.isPending && editing}
            >
              {editing ? '保存' : '編集'}
            </Button>
            {editing && (
              <Button onClick={handleCancel}>キャンセル</Button>
            )}
          </>
        )}
      </Space>

      {!editing ? (
        <BalancedDescriptions bordered column={columnCount} size="small">
          <Descriptions.Item label="氏名">{user.name}</Descriptions.Item>
          <Descriptions.Item label="メールアドレス">{user.email}</Descriptions.Item>
          <Descriptions.Item label="権限">{getRoleText(user.role)}</Descriptions.Item>
          <Descriptions.Item label="部署">{user.department || '-'}</Descriptions.Item>
          <Descriptions.Item label="役職">{user.position || '-'}</Descriptions.Item>
          <Descriptions.Item label="電話番号">{user.phone || '-'}</Descriptions.Item>
          <Descriptions.Item label="会社名">{user.companyName || user.companyId}</Descriptions.Item>
          <Descriptions.Item label="最終更新">{formatDateTime(user.updatedAt)}</Descriptions.Item>
        </BalancedDescriptions>
      ) : (
        <Form
          form={profileForm}
          layout="vertical"
          onFinish={handleProfileSubmit}
          initialValues={{
            ...user,
            avatarUrl: effectiveAvatarUrl ?? undefined,
          }}
        >
          <Row gutter={[24, 16]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="name"
                label="氏名"
                rules={[
                  { required: true, message: '氏名を入力してください' },
                  { max: 100, message: '氏名は100文字以内で入力してください' },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="email" label="メールアドレス">
                <Input disabled />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[24, 16]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="department"
                label="部署"
                rules={[{ max: 100, message: '部署名は100文字以内で入力してください' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="position"
                label="役職"
                rules={[{ max: 100, message: '役職は100文字以内で入力してください' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[24, 16]}>
            <Col xs={24} md={12}>
              <Form.Item
                name="phone"
                label="電話番号"
                rules={[
                  { max: 20, message: '電話番号は20文字以内で入力してください' },
                  phoneNumberRule(),
                ]}
              >
                <PhoneInput />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="avatarUrl"
                label="プロフィール画像URL"
                rules={[{ type: 'url', message: '有効なURLを入力してください' }]}
              >
                <Space.Compact style={{ width: '100%' }}>
                  <Input placeholder="https://example.com/avatar.jpg" />
                  <Button
                    icon={<UploadOutlined />}
                    onClick={() => setAvatarModalOpen(true)}
                  >
                    アップロード
                  </Button>
                </Space.Compact>
              </Form.Item>
            </Col>
          </Row>

          <Space style={{ justifyContent: 'flex-end', width: '100%' }}>
            <Button onClick={handleCancel}>キャンセル</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={updateProfileMutation.isPending}
            >
              更新
            </Button>
          </Space>
        </Form>
      )}
    </Space>
  );

  const securityContent = (
    <Row gutter={[24, 24]}>
      <Col xs={24} md={12}>
        <Card type="inner" title={<Space><LockOutlined />パスワード変更</Space>}>
          {isDemoReadOnly ? (
            <Alert
              message="デモアカウント"
              description="デモアカウントではパスワードの変更はできません。"
              type="info"
              showIcon
            />
          ) : (
            <Form
              form={passwordForm}
              layout="vertical"
              onFinish={handlePasswordSubmit}
            >
              <Form.Item
                name="currentPassword"
                label="現在のパスワード"
                rules={[{ required: true, message: '現在のパスワードを入力してください' }]}
              >
                <Input.Password autoComplete="current-password" />
              </Form.Item>
              <Form.Item
                name="newPassword"
                label="新しいパスワード"
                rules={[
                  { required: true, message: '新しいパスワードを入力してください' },
                  { min: 8, message: 'パスワードは8文字以上で入力してください' },
                  { max: 100, message: 'パスワードは100文字以内で入力してください' },
                ]}
              >
                <Input.Password autoComplete="new-password" />
              </Form.Item>
              <Form.Item
                name="confirmPassword"
                label="新しいパスワード（確認）"
                dependencies={['newPassword']}
                rules={[
                  { required: true, message: 'パスワードを再入力してください' },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('パスワードが一致しません'));
                    },
                  }),
                ]}
              >
                <Input.Password autoComplete="new-password" />
              </Form.Item>
              <Space>
                <Button onClick={() => passwordForm.resetFields()}>リセット</Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={changePasswordMutation.isPending}
                >
                  変更
                </Button>
              </Space>
            </Form>
          )}
        </Card>
      </Col>
    </Row>
  );

  const displayContent = (
    <Form
      form={displayForm}
      layout="vertical"
      onFinish={handleDisplaySubmit}
      initialValues={{}}
    >
      {isDemoReadOnly && (
        <Alert
          message="デモアカウント"
          description="デモアカウントでは表示設定の変更はできません。"
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}
      <Card type="inner" title={<Space><EyeOutlined />テーマとフォント</Space>}>
        <Form.Item name={['appearance', 'theme']} label="テーマ">
          <Radio.Group disabled={isDemoReadOnly}>
            <Radio.Button value="light">ライト</Radio.Button>
            <Radio.Button value="mild">マイルド</Radio.Button>
            <Radio.Button value="dark">ダーク</Radio.Button>
            <Radio.Button value="system">システムに従う</Radio.Button>
          </Radio.Group>
        </Form.Item>
        <Form.Item name={['appearance', 'fontSize']} label="フォントサイズ">
          <Radio.Group disabled={isDemoReadOnly}>
            <Radio.Button value="small">小</Radio.Button>
            <Radio.Button value="medium">標準</Radio.Button>
            <Radio.Button value="large">大</Radio.Button>
          </Radio.Group>
        </Form.Item>
        <Form.Item name={['appearance', 'compactSidebar']} valuePropName="checked" label="コンパクトサイドバー">
          <Switch disabled={isDemoReadOnly} />
        </Form.Item>
        <Form.Item name={['appearance', 'showTooltips']} valuePropName="checked" label="ツールチップを表示">
          <Switch disabled={isDemoReadOnly} />
        </Form.Item>
        <Form.Item name={['appearance', 'reduceMotion']} valuePropName="checked" label="アニメーションを減らす">
          <Switch disabled={isDemoReadOnly} />
        </Form.Item>
      </Card>

      <Card type="inner" title="ダッシュボード設定" style={{ marginTop: 24 }}>
        <Form.Item label="はじめてガイド">
          <Space direction="vertical" size="small">
            <Text type="secondary">
              ダッシュボードに表示される初期セットアップガイドを再表示します。
            </Text>
            <Button
              icon={<RocketOutlined />}
              onClick={() => {
                localStorage.removeItem('onboarding_dismissed');
                messageApi.success('はじめてガイドを再表示しました。ダッシュボードに戻ると表示されます。');
              }}
            >
              はじめてガイドを再表示する
            </Button>
          </Space>
        </Form.Item>
      </Card>

      {!isDemoReadOnly && (
        <Space style={{ marginTop: 24 }}>
          <Button onClick={() => displayForm.resetFields()}>リセット</Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={updatePreferencesMutation.isPending}
          >
            保存
          </Button>
        </Space>
      )}
    </Form>
  );

  const activityContent = (
    <Card type="inner" title={<Space><HistoryOutlined />最近のアクティビティ</Space>}>
      {activityEntries.every((entry) => !entry.timestamp) ? (
        <Empty description="最近の活動履歴はありません" />
      ) : (
        <List
          itemLayout="horizontal"
          dataSource={activityEntries.filter((entry) => entry.timestamp)}
          renderItem={(item) => (
            <List.Item>
              <List.Item.Meta
                title={item.title}
                description={
                  <Space direction="vertical" size={0}>
                    <Text>{formatDateTime(item.timestamp)}</Text>
                    {item.description && <Text type="secondary">{item.description}</Text>}
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      )}
    </Card>
  );

  const tabItems: TabsProps['items'] = [
    {
      key: 'basic',
      label: (
        <Space>
          <IdcardOutlined />
          <span>基本情報</span>
        </Space>
      ),
      children: basicInfoContent,
    },
    {
      key: 'documents',
      label: (
        <Space>
          <UploadOutlined />
          <span>書類</span>
        </Space>
      ),
      children: (
        <DocumentListSection
          entityType="user"
          entityId={user.id}
          allowCreate={!isDemoReadOnly && user.role !== 'viewer'}
        />
      ),
    },
    {
      key: 'security',
      label: (
        <Space>
          <LockOutlined />
          <span>パスワード・セキュリティ</span>
        </Space>
      ),
      children: securityContent,
    },
    {
      key: 'appearance',
      label: (
        <Space>
          <EyeOutlined />
          <span>表示設定</span>
        </Space>
      ),
      children: displayContent,
    },
    {
      key: 'activity',
      label: (
        <Space>
          <HistoryOutlined />
          <span>活動履歴</span>
        </Space>
      ),
      children: activityContent,
    },
  ];

  return (
    <>
      {contextHolder}
      <div className="max-w-5xl mx-auto p-6">
        <Card>
          <Tabs
            activeKey={activeTabKey}
            onChange={(key) => setActiveTabKey(key as string)}
            items={tabItems}
            destroyOnHidden={false}
          />
        </Card>
      </div>
      <AvatarUploadModal
        open={avatarModalOpen}
        onClose={() => setAvatarModalOpen(false)}
        onSuccess={handleAvatarUploadSuccess}
        currentAvatarUrl={effectiveAvatarUrl ?? undefined}
        uploading={uploadAvatarMutation.isPending}
      />
    </>
  );
};

export default ProfileManagement;
